import React, { createRef, Component } from 'react';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage } from '@atlassian/jira-intl';
import {
	Button,
	HoverObserver,
	InlineDialog,
	ScrollLock,
} from '@atlassian/jira-portfolio-3-common';
import { INLINE_CREATE_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/inline-create';
import ChangeIndicator from '@atlassian/jira-portfolio-3-portfolio/src/common/view/change-indicator';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, State } from './types';

const FLYOUT_MAX_WIDTH = 800;
const FLYOUT_MAX_HEIGHT = 400;

// eslint-disable-next-line jira/react/no-class-components
export default class DependenciesCell extends Component<Props, State> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/sort-comp
	ref = createRef<any>();

	state = {
		isHovered: false,
		isOpen: false,
		left: 0,
		top: 0,
		bottom: Number.POSITIVE_INFINITY,
	};

	componentDidUpdate() {
		if (this.state.isOpen && this.ref.current) {
			const { left, top, bottom } = this.ref.current.getBoundingClientRect();
			if (this.state.top !== top || this.state.bottom !== bottom) {
				// Since we can't reposition the opened dialog, we close and re-open to fix the position
				this.setState({ left, top, bottom, isOpen: false }, () => {
					this.setState({ isOpen: true });
				});
			}
		}
	}

	onClose = ({ event }: { isOpen: boolean; event: Event }) => {
		if (
			this.props.isDialogLocked ||
			(this.ref.current && this.ref.current.contains(event.srcElement))
		) {
			return;
		}
		this.setState({ isOpen: false });
	};

	onHoverChanged = (isHovered: boolean) => {
		this.setState({ isHovered });
	};

	onToggle = () => {
		this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
	};

	getIssueLinksCount = () =>
		this.props.externalIssueLinks.length + this.props.internalIssueLinks.length;

	renderIssueCount = () => {
		const { messages } = this.props;
		const { isHovered, isOpen } = this.state;
		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styles.issuesCount}
				onClick={this.onToggle}
				tabIndex={-1}
				role="button"
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onKeyDown={() => {}}
			>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.issuesLabel}>
					<FormattedMessage {...messages.issues} values={{ count: this.getIssueLinksCount() }} />
				</div>
				{(isHovered || isOpen) && (
					<div>
						<ChevronDownIcon label="" size="large" />
					</div>
				)}
			</div>
		);
	};

	renderAddDependency = () => {
		const { isReadOnly, messages } = this.props;
		const { isHovered, isOpen } = this.state;
		return !isReadOnly && (isHovered || isOpen) ? (
			<Button appearance="link" onClick={this.onToggle}>
				<FormattedMessage {...messages.addDependency} />
			</Button>
		) : null;
	};

	renderContent = () => {
		const { isChanged, isOptimizedMode } = this.props;
		return (
			<>
				{this.getIssueLinksCount() > 0 ? this.renderIssueCount() : this.renderAddDependency()}
				{isChanged && !isOptimizedMode ? <ChangeIndicator /> : null}
			</>
		);
	};

	render() {
		const { issue, width } = this.props;
		if (issue.id === INLINE_CREATE_ID) {
			return null;
		}
		const { isOpen, left, bottom } = this.state;
		const flyoutPosition = () => {
			if (
				window.innerHeight - bottom < FLYOUT_MAX_HEIGHT &&
				window.innerWidth - left < FLYOUT_MAX_WIDTH
			)
				return 'top-end';
			if (window.innerHeight - bottom < FLYOUT_MAX_HEIGHT) return 'top-start';
			if (window.innerWidth - left < FLYOUT_MAX_WIDTH) return 'bottom-end';
			return 'bottom-start';
		};

		const dialog = (
			<InlineDialog
				content={
					ff('list-view-add-dependency-styling-fix_9ibdi') && this.props.renderDialogContent
						? this.props.renderDialogContent()
						: this.props.dialogContent
				}
				isOpen={isOpen}
				onClose={this.onClose}
				placement={flyoutPosition()}
				maxWidth={FLYOUT_MAX_WIDTH}
			>
				<div />
			</InlineDialog>
		);
		const content = (
			<div
				ref={this.ref}
				style={
					// position: 'absolute' is used to force width
					// forcing width is required to have chevron and change indicator
					// to be in proper place if width of the column is too small
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					{ width, position: 'absolute' }
				}
			>
				<HoverObserver onHoverChanged={this.onHoverChanged}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={styles.container}>{this.renderContent()}</div>
				</HoverObserver>
				{ff('com.atlassian.rm.jpo.transposition') ? (
					<ScrollLock axis="vertical" enabled={isOpen} />
				) : (
					<ScrollLock enabled={isOpen} />
				)}
				{isOpen && dialog}
			</div>
		);
		return content;
	}
}
